/**
 * Cache integrity verification
 * Handles checksum validation and integrity checks for cache entries
 */

import { systemLogger } from '@/lib/logging';
import type { CacheEntry } from '../types/cache-types';

export class CacheIntegrity {
  /**
   * Create checksum for cache entry
   */
  async createChecksum(
    data: string,
    userId: string,
    timestamp: number,
    encrypted: { data: string; iv: string; tag: string }
  ): Promise<string> {
    try {
      const checksumData = JSON.stringify({
        data,
        userId,
        timestamp,
        encrypted: encrypted.data + encrypted.iv + encrypted.tag,
      });

      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(checksumData);

      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
      const hashArray = new Uint8Array(hashBuffer);

      return Array.from(hashArray)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      systemLogger.error('Failed to create cache checksum:', error);
      throw new Error('Checksum creation failed');
    }
  }

  /**
   * Verify integrity of cache entry
   */
  async verifyIntegrity(entry: CacheEntry): Promise<boolean> {
    try {
      const expectedChecksum = await this.createChecksum(
        JSON.stringify(entry.data),
        entry.userId,
        entry.timestamp,
        {
          data: entry.encrypted,
          iv: entry.iv,
          tag: entry.tag,
        }
      );

      const isValid = expectedChecksum === entry.checksum;

      if (!isValid) {
        systemLogger.warn('Cache integrity check failed', {
          userId: entry.userId,
          timestamp: entry.timestamp,
          expected: expectedChecksum,
          actual: entry.checksum,
        });
      }

      return isValid;
    } catch (error) {
      systemLogger.error('Cache integrity verification failed:', error);
      return false;
    }
  }

  /**
   * Validate cache entry structure
   */
  validateEntryStructure(entry: unknown): entry is CacheEntry {
    if (!entry || typeof entry !== 'object') {
      return false;
    }

    const e = entry as Record<string, unknown>;

    return (
      typeof e['data'] === 'object' &&
      typeof e['encrypted'] === 'string' &&
      typeof e['iv'] === 'string' &&
      typeof e['tag'] === 'string' &&
      typeof e['timestamp'] === 'number' &&
      typeof e['ttl'] === 'number' &&
      typeof e['accessCount'] === 'number' &&
      typeof e['userId'] === 'string' &&
      typeof e['checksum'] === 'string'
    );
  }

  /**
   * Check if cache entry has expired
   */
  isExpired(entry: CacheEntry): boolean {
    return Date.now() > (entry.timestamp + entry.ttl);
  }

  /**
   * Sanitize cache entry for logging (remove sensitive data)
   */
  sanitizeForLogging(entry: CacheEntry): Partial<CacheEntry> {
    return {
      timestamp: entry.timestamp,
      ttl: entry.ttl,
      accessCount: entry.accessCount,
      userId: entry.userId,
      // Omit sensitive data and checksum
    };
  }
}